import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { organizationLicenseService, licensePlanService, usageService, organizationService, storageService } from '../api';
import { Button, ThemedText, ActionMenu, ConfirmDialog } from '../components';
import { extractItems } from '../utils/responseUtils';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { ModalOverlay as Overlay, ModalHeader, ModalFooter } from '../components/ModalStyles';

// --- Helpers ---

const STATUS_COLORS = {
  Active: { color: '#22c55e', bg: '#22c55e20' },
  Trial: { color: '#3b82f6', bg: '#3b82f620' },
  Expired: { color: '#ef4444', bg: '#ef444420' },
  Suspended: { color: '#f59e0b', bg: '#f59e0b20' },
  Cancelled: { color: '#6b7280', bg: '#6b728020' },
  GracePeriod: { color: '#f97316', bg: '#f9731620' },
};

const getUsageColor = (pct) => {
  if (pct > 95) return '#ef4444';
  if (pct > 80) return '#f97316';
  if (pct > 60) return '#f59e0b';
  return '#22c55e';
};

const fmtDate = (d) => {
  if (!d) return '--';
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(i > 1 ? 1 : 0)} ${units[i]}`;
};

const INITIAL_ASSIGN = {
  organizationId: '',
  licensePlanId: '',
  startDate: new Date().toISOString().slice(0, 10),
  expiryDate: '',
  overrideMaxSubmissionsPerMonth: '',
  overrideMaxTotalSubmissions: '',
  overrideMaxUsers: '',
  overrideMaxFormSchemas: '',
  notes: '',
};

// --- Styled Components ---

const FiltersRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary || p.theme.colors.tint};
  }
`;

const TableContainer = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border-bottom: 2px solid ${p => p.theme.colors.border};
`;

const Th = styled.th`
  text-align: left;
  padding: ${p => p.theme.spacing.md};
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  white-space: nowrap;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${p => p.theme.colors.border};
  transition: background-color 0.2s ease;
  &:hover { background-color: ${p => p.theme.colors.background}; }
  &:last-child { border-bottom: none; }
`;

const Td = styled.td`
  padding: ${p => p.theme.spacing.md};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${p => p.bg || '#22c55e20'};
  color: ${p => p.color || '#22c55e'};
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  min-width: 100px;
  height: 8px;
  background-color: ${p => p.theme.colors.border};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  border-radius: 4px;
  width: ${p => Math.min(p.pct, 100)}%;
  background-color: ${p => p.barColor};
  transition: width 0.3s ease;
`;

const UsageCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
`;

const UsageLabel = styled.span`
  font-size: 11px;
  color: ${p => p.textColor || p.theme.colors.textSecondary};
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
`;

const OrgLink = styled.span`
  cursor: pointer;
  color: ${p => p.theme.colors.primary || p.theme.colors.tint};
  &:hover {
    text-decoration: underline;
  }
`;

// --- Modal Styled Components ---

const ModalContent = styled.div`
  background-color: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  max-width: 640px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${p => p.theme.shadows?.xl || '0 25px 50px rgba(0,0,0,0.25)'};
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  margin-top: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.sm};
  padding-bottom: 6px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${p => p.cols || '1fr 1fr'};
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.sm};
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  font-size: 12px;
`;

const StyledInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s ease;
  &:focus {
    border-color: ${p => p.theme.colors.primary || p.theme.colors.tint};
    box-shadow: 0 0 0 3px ${p => (p.theme.colors.primary || p.theme.colors.tint) + '20'};
  }
`;

const ModalSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: ${p => p.theme.colors.primary || p.theme.colors.tint};
    box-shadow: 0 0 0 3px ${p => (p.theme.colors.primary || p.theme.colors.tint) + '20'};
  }
`;

const StyledTextarea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  resize: vertical;
  min-height: 60px;
  outline: none;
  font-family: inherit;
  &:focus {
    border-color: ${p => p.theme.colors.primary || p.theme.colors.tint};
  }
`;

// --- Component ---

const LicenseManagementPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isSuperAdmin } = useRole();

  const [licenses, setLicenses] = useState([]);
  const [plans, setPlans] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [usageMap, setUsageMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Assign modal
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ ...INITIAL_ASSIGN });
  const [saving, setSaving] = useState(false);

  // Extend modal
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendDate, setExtendDate] = useState('');

  // Confirm dialogs
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [reactivateTarget, setReactivateTarget] = useState(null);

  // Recalculate storage
  const [recalculatingOrgId, setRecalculatingOrgId] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [licRes, planRes, orgRes, usageRes] = await Promise.all([
        organizationLicenseService.getAllLicenses(),
        licensePlanService.getPlans(),
        organizationService.getOrganizations(),
        usageService.getAllOrganizationsUsage().catch(() => null),
      ]);
      setLicenses(extractItems(licRes));
      setPlans(extractItems(planRes));
      setOrganizations(extractItems(orgRes));

      // Build usage lookup by orgId
      const usages = extractItems(usageRes);
      const map = {};
      usages.forEach(u => { map[u.organizationId] = u; });
      setUsageMap(map);
    } catch (err) {
      console.error('Failed to fetch license data:', err);
      toast.error('Failed to load license data');
    } finally {
      setLoading(false);
    }
  };

  // --- Filtering ---

  const filtered = licenses.filter(lic => {
    if (filterPlan && String(lic.licensePlanId) !== filterPlan) return false;
    if (filterStatus && lic.status !== filterStatus) return false;
    return true;
  });

  // --- Assign License ---

  const openAssign = () => {
    setAssignForm({ ...INITIAL_ASSIGN });
    setAssignOpen(true);
  };

  const closeAssign = () => {
    setAssignOpen(false);
  };

  const updateAssign = (field, value) => setAssignForm(prev => ({ ...prev, [field]: value }));

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assignForm.organizationId) { toast.error('Please select an organization'); return; }
    if (!assignForm.licensePlanId) { toast.error('Please select a plan'); return; }

    try {
      setSaving(true);
      const payload = {
        organizationId: Number(assignForm.organizationId),
        licensePlanId: Number(assignForm.licensePlanId),
        startDate: assignForm.startDate || undefined,
        expiryDate: assignForm.expiryDate || undefined,
        notes: assignForm.notes || undefined,
      };
      if (assignForm.overrideMaxSubmissionsPerMonth) payload.overrideMaxSubmissionsPerMonth = Number(assignForm.overrideMaxSubmissionsPerMonth);
      if (assignForm.overrideMaxTotalSubmissions) payload.overrideMaxTotalSubmissions = Number(assignForm.overrideMaxTotalSubmissions);
      if (assignForm.overrideMaxUsers) payload.overrideMaxUsers = Number(assignForm.overrideMaxUsers);
      if (assignForm.overrideMaxFormSchemas) payload.overrideMaxFormSchemas = Number(assignForm.overrideMaxFormSchemas);

      await organizationLicenseService.assignLicense(payload);
      toast.success('License assigned successfully');
      closeAssign();
      fetchAll();
    } catch (err) {
      console.error('Failed to assign license:', err);
      toast.error(err.response?.data?.message || 'Failed to assign license');
    } finally {
      setSaving(false);
    }
  };

  // --- Quick Actions ---

  const handleSuspend = async () => {
    if (!suspendTarget) return;
    try {
      await organizationLicenseService.suspendLicense(suspendTarget);
      toast.success('License suspended');
      setSuspendTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to suspend license');
    }
  };

  const handleReactivate = async () => {
    if (!reactivateTarget) return;
    try {
      await organizationLicenseService.reactivateLicense(reactivateTarget);
      toast.success('License reactivated');
      setReactivateTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reactivate license');
    }
  };

  const handleExtend = async () => {
    if (!extendTarget || !extendDate) return;
    try {
      await organizationLicenseService.extendLicense(extendTarget, extendDate);
      toast.success('License extended');
      setExtendTarget(null);
      setExtendDate('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to extend license');
    }
  };

  const handleRecalculateStorage = async (orgId) => {
    try {
      setRecalculatingOrgId(orgId);
      const result = await storageService.reconcileStorage(orgId);
      const data = result?.data || result;

      if (data.recordsCreated > 0) {
        toast.success(
          `Storage recalculated: Found ${data.missingRecordsFound} missing files, created ${data.recordsCreated} records. ` +
          `Total storage: ${formatBytes(data.totalBytesInDatabase)}`
        );
      } else if (data.missingRecordsFound === 0) {
        toast.info(`Storage is already up to date. Total: ${formatBytes(data.totalBytesInDatabase)}`);
      } else {
        toast.warning(`Recalculation complete but ${data.recordsFailed} records failed to create.`);
      }

      // Refresh usage data
      fetchAll();
    } catch (err) {
      console.error('Failed to recalculate storage:', err);
      toast.error(err.response?.data?.message || 'Failed to recalculate storage');
    } finally {
      setRecalculatingOrgId(null);
    }
  };

  // --- Render helpers ---

  const getStatusBadge = (status) => {
    const s = STATUS_COLORS[status] || STATUS_COLORS.Cancelled;
    return <Badge bg={s.bg} color={s.color}>{status}</Badge>;
  };

  const renderUsageBar = (lic) => {
    const usage = usageMap[lic.organizationId];
    const subData = usage?.submissionsThisMonth;
    const used = subData?.current ?? 0;
    const limit = subData?.limit ?? lic.overrideMaxSubmissionsPerMonth ?? 0;
    const pct = limit > 0 ? Math.round((used / limit) * 100) : 0;
    const barColor = getUsageColor(pct);
    const displayLimit = limit > 0 ? limit : 'Unlimited';

    return (
      <UsageCell>
        <UsageLabel theme={theme} textColor={limit > 0 ? barColor : undefined}>{used} / {displayLimit}{limit > 0 ? ` (${pct}%)` : ''}</UsageLabel>
        {limit > 0 && (
          <ProgressBarOuter theme={theme}>
            <ProgressBarInner pct={pct} barColor={barColor} />
          </ProgressBarOuter>
        )}
      </UsageCell>
    );
  };

  const buildActions = (lic) => {
    const actions = [];
    if (lic.status === 'Active' || lic.status === 'Trial' || lic.status === 'GracePeriod') {
      actions.push({ label: 'Suspend', onClick: () => setSuspendTarget(lic.id), variant: 'danger' });
    }
    if (lic.status === 'Suspended') {
      actions.push({ label: 'Reactivate', onClick: () => setReactivateTarget(lic.id) });
    }
    if (lic.status !== 'Cancelled') {
      actions.push({ label: 'Extend Expiry', onClick: () => { setExtendTarget(lic.id); setExtendDate(lic.expiryDate?.slice(0, 10) || ''); } });
    }
    // Recalculate storage option
    actions.push({
      label: recalculatingOrgId === lic.organizationId ? 'Recalculating...' : 'Recalculate Storage',
      onClick: () => handleRecalculateStorage(lic.organizationId),
      disabled: recalculatingOrgId !== null,
    });
    return actions;
  };

  const getOrgName = (lic) => lic.organization?.name || lic.organizationName || organizations.find(o => o.id === lic.organizationId)?.name || `Org #${lic.organizationId}`;
  const getPlanName = (lic) => lic.licensePlan?.name || lic.planName || plans.find(p => p.id === lic.licensePlanId)?.name || '--';

  const uniqueStatuses = [...new Set(licenses.map(l => l.status).filter(Boolean))];

  // --- Access guard ---
  if (!isSuperAdmin()) {
    return (
      <Layout currentPath="/license-management">
        <PageContainer theme={theme}>
          <ThemedText type="title">Access Denied</ThemedText>
          <ThemedText>You do not have permission to access this page.</ThemedText>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout currentPath="/license-management">
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <div>
            <ThemedText type="title">License Management</ThemedText>
            <ThemedText type="subtitle">Manage organization license assignments and usage</ThemedText>
          </div>
          <Button onClick={openAssign}>+ Assign License</Button>
        </PageHeader>

        {/* Filters */}
        <FiltersRow theme={theme}>
          <Select theme={theme} value={filterPlan} onChange={e => setFilterPlan(e.target.value)}>
            <option value="">All Plans</option>
            {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select theme={theme} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FiltersRow>

        {/* Table */}
        <TableContainer theme={theme}>
          {loading ? (
            <LoadingState theme={theme}><ThemedText>Loading licenses...</ThemedText></LoadingState>
          ) : filtered.length === 0 ? (
            <EmptyState theme={theme}><ThemedText>No licenses found</ThemedText></EmptyState>
          ) : (
            <Table>
              <Thead theme={theme}>
                <tr>
                  <Th theme={theme}>Organization</Th>
                  <Th theme={theme}>Plan</Th>
                  <Th theme={theme}>Status</Th>
                  <Th theme={theme}>Submissions</Th>
                  <Th theme={theme}>Expiry Date</Th>
                  <Th theme={theme} style={{ width: '50px' }}></Th>
                </tr>
              </Thead>
              <Tbody>
                {filtered.map(lic => (
                  <Tr key={lic.id} theme={theme}>
                    <Td theme={theme}>
                      <OrgLink
                        theme={theme}
                        onClick={() => navigate(`/license-management/${lic.id}`)}
                      >
                        <ThemedText type="defaultSemiBold">{getOrgName(lic)}</ThemedText>
                      </OrgLink>
                    </Td>
                    <Td theme={theme}>{getPlanName(lic)}</Td>
                    <Td theme={theme}>{getStatusBadge(lic.status)}</Td>
                    <Td theme={theme}>{renderUsageBar(lic)}</Td>
                    <Td theme={theme}>{fmtDate(lic.expiryDate)}</Td>
                    <Td theme={theme}>
                      <ActionMenu actions={buildActions(lic)} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </TableContainer>

        {/* Assign License Modal */}
        <Overlay theme={theme} isOpen={assignOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Assign License</ThemedText>
              <Button variant="secondary" size="sm" onClick={closeAssign}>X</Button>
            </ModalHeader>

            <form onSubmit={handleAssign}>
              <SectionLabel theme={theme}>Organization &amp; Plan</SectionLabel>
              <FormRow theme={theme}>
                <FormGroup>
                  <Label theme={theme}>Organization *</Label>
                  <ModalSelect theme={theme} value={assignForm.organizationId} onChange={e => updateAssign('organizationId', e.target.value)} required>
                    <option value="">Select organization</option>
                    {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </ModalSelect>
                </FormGroup>
                <FormGroup>
                  <Label theme={theme}>License Plan *</Label>
                  <ModalSelect theme={theme} value={assignForm.licensePlanId} onChange={e => updateAssign('licensePlanId', e.target.value)} required>
                    <option value="">Select plan</option>
                    {plans.filter(p => p.isActive !== false).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </ModalSelect>
                </FormGroup>
              </FormRow>

              <SectionLabel theme={theme}>Dates</SectionLabel>
              <FormRow theme={theme} cols="1fr 1fr">
                <FormGroup>
                  <Label theme={theme}>Start Date</Label>
                  <StyledInput theme={theme} type="date" value={assignForm.startDate} onChange={e => updateAssign('startDate', e.target.value)} />
                </FormGroup>
                <FormGroup>
                  <Label theme={theme}>Expiry Date (auto from plan, or override)</Label>
                  <StyledInput theme={theme} type="date" value={assignForm.expiryDate} onChange={e => updateAssign('expiryDate', e.target.value)} />
                </FormGroup>
              </FormRow>

              <SectionLabel theme={theme}>Overrides (optional)</SectionLabel>
              <FormRow theme={theme} cols="1fr 1fr 1fr">
                <FormGroup>
                  <Label theme={theme}>Max Submissions/Mo</Label>
                  <StyledInput theme={theme} type="number" min="0" placeholder="Plan default" value={assignForm.overrideMaxSubmissionsPerMonth} onChange={e => updateAssign('overrideMaxSubmissionsPerMonth', e.target.value)} />
                </FormGroup>
                <FormGroup>
                  <Label theme={theme}>Max Total Submissions</Label>
                  <StyledInput theme={theme} type="number" min="0" placeholder="Plan default" value={assignForm.overrideMaxTotalSubmissions} onChange={e => updateAssign('overrideMaxTotalSubmissions', e.target.value)} />
                </FormGroup>
                <FormGroup>
                  <Label theme={theme}>Max Users</Label>
                  <StyledInput theme={theme} type="number" min="0" placeholder="Plan default" value={assignForm.overrideMaxUsers} onChange={e => updateAssign('overrideMaxUsers', e.target.value)} />
                </FormGroup>
              </FormRow>
              <FormRow theme={theme} cols="1fr 1fr">
                <FormGroup>
                  <Label theme={theme}>Max Forms</Label>
                  <StyledInput theme={theme} type="number" min="0" placeholder="Plan default" value={assignForm.overrideMaxFormSchemas} onChange={e => updateAssign('overrideMaxFormSchemas', e.target.value)} />
                </FormGroup>
              </FormRow>

              <FormGroup style={{ marginTop: theme.spacing.sm }}>
                <Label theme={theme}>Notes</Label>
                <StyledTextarea theme={theme} value={assignForm.notes} onChange={e => updateAssign('notes', e.target.value)} placeholder="Optional notes about this license assignment" />
              </FormGroup>

              <ModalFooter theme={theme}>
                <Button type="button" variant="secondary" onClick={closeAssign}>Cancel</Button>
                <Button type="submit" disabled={saving}>{saving ? 'Assigning...' : 'Assign License'}</Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Overlay>

        {/* Extend Expiry Modal */}
        <Overlay theme={theme} isOpen={extendTarget !== null}>
          <ModalContent theme={theme} style={{ maxWidth: 400 }}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Extend Expiry</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setExtendTarget(null); setExtendDate(''); }}>X</Button>
            </ModalHeader>
            <FormGroup>
              <Label theme={theme}>New Expiry Date</Label>
              <StyledInput theme={theme} type="date" value={extendDate} onChange={e => setExtendDate(e.target.value)} />
            </FormGroup>
            <ModalFooter theme={theme}>
              <Button variant="secondary" onClick={() => { setExtendTarget(null); setExtendDate(''); }}>Cancel</Button>
              <Button onClick={handleExtend} disabled={!extendDate}>Extend</Button>
            </ModalFooter>
          </ModalContent>
        </Overlay>

        {/* Confirm Dialogs */}
        <ConfirmDialog
          isOpen={suspendTarget !== null}
          title="Suspend License"
          message="Are you sure you want to suspend this organization's license? They will lose access to licensed features."
          onConfirm={handleSuspend}
          onCancel={() => setSuspendTarget(null)}
        />
        <ConfirmDialog
          isOpen={reactivateTarget !== null}
          title="Reactivate License"
          message="Are you sure you want to reactivate this organization's license?"
          onConfirm={handleReactivate}
          onCancel={() => setReactivateTarget(null)}
        />
      </PageContainer>
    </Layout>
  );
};

export default LicenseManagementPage;
export { LicenseManagementPage };
